import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  makeStyles,
  TextField,
  Theme
} from "@material-ui/core";
import clsx from 'clsx';
import React, {useEffect, useState} from "react";
import CloseIcon from '@material-ui/icons/Close';
import RemoveIcon from '@material-ui/icons/Remove';
import Draggable from "react-draggable";
import { headerHeight } from "./app";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    draggableTitleClose: {
      color: theme.palette.grey[500],
      marginRight: theme.spacing(1)
    },
    draggableTitleMinimize: {
      color: theme.palette.grey[500],
    },
    draggableContent: {
      padding: theme.spacing(0),
      paddingTop: "0px !important",
      height: "fit-content",
      width: "500px",
      maxWidth: "500px",
      marginTop: `${headerHeight + theme.spacing(1)}px`
    },
    draggableContentSlim: {
      height: "min(75vh, 600px)",
      width: "400px",
      maxWidth: "400px"
    },
    draggableContentLeft: {
      marginLeft: theme.spacing(1)
    },
    draggableTopDiv: {
      display: "flex",
      alignItems: "center"
    },
    draggableBackdropPaper: {
      height: "inherit"
    },
    draggableBackdropRounded: {
      borderRadius: theme.spacing(0.5),
      height: "inherit"
    },
    draggableRoundCorner: {
      backgroundColor: "transparent"
    },
    draggableTitle: {
      userSelect: "none",
      cursor: "grab",
      flex: 1
    },
    draggableTitleDown: {
      cursor: "grabbing"
    },
    progressContent: {
      padding: `${theme.spacing(3)}px !important`,
    },
    progressNoTransition: {
      transition: "none"
    }
  }),
);

export const PromptDialog = props => {
  const {open, title, prompt, button, onCancel, onSubmit} = props;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle>
        {title}
      </DialogTitle>

      <DialogContent>
        <DialogContentText>
          {prompt}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          color="primary"
        >
          {button}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AlertDialog = props => {
  const {open, title, handleClose} = props;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {title}
      </DialogTitle>
      <DialogContent>
        {props.children}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const TextDialog = props => {
  const [text, setText] = useState(props.default);
  const [disabled, setDisabled] = useState(true);

  const handleTextChange = (event) => {
    setText(event.target.value);
  };

  useEffect(() => {
    if (text.trim() !== '') {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [text]);

  const handleCancel = () => {
    props.onCancel();
    setText(props.default);
  };

  const handleSubmit = () => {
    const trimmedText = text.trim();

    props.onSubmit(trimmedText);

    if (props.clear) {
      setText('');
    } else {
      setText(trimmedText);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (text === '') {
        return;
      }

      handleSubmit();
    }
  };

  return (
    <Dialog open={props.open} aria-labelledby="form-dialog-title">
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {props.prompt}
        </DialogContentText>
        <TextField
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          autoFocus
          placeholder={props.default}
          margin="dense"
          label={props.label}
          type="text"
          fullWidth
          value={text}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={disabled}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const DraggableDialog = props => {
  const {open, title, narrow, onClose, onMinimize} = props;
  const classes = useStyles();

  const [mouseDown, setMouseDown] = useState(false);

  const onDragMouseDown = () => {
    setMouseDown(true);
  };

  const onDragMouseUp = () => {
    setMouseDown(false);
  };

  return (
    <Draggable handle=".draggable-dialog-title">
      <Dialog
        fullScreen
        maxWidth="lg"
        open={open}
        aria-labelledby="draggable-dialog-title"
        className={clsx(classes.draggableContent, classes.draggableContentLeft, {
          [classes.draggableContentSlim]: narrow
        })}
        classes={{
          paperFullScreen: classes.draggableBackdropRounded,
          scrollPaper: classes.draggableBackdropPaper
        }}
        BackdropProps={{
          className: classes.draggableRoundCorner
        }}
      >
        <div
          onMouseDown={onDragMouseDown}
          onMouseUp={onDragMouseUp}
          className={classes.draggableTopDiv}
        >
          {/* Draggable title */}
          <DialogTitle
            className={clsx("draggable-dialog-title", classes.draggableTitle, {
              [classes.draggableTitleDown]: mouseDown,
            })}
          >
            {title}
          </DialogTitle>

          {/* Optional minimize button */}
          {onMinimize && (<IconButton
            aria-label="minimize"
            className={classes.draggableTitleMinimize}
            onClick={onMinimize}
          >
            <RemoveIcon fontSize="small"/>
          </IconButton>)}

          {/* Close button */}
          <IconButton
            aria-label="close"
            className={classes.draggableTitleClose}
            onClick={onClose}
          >
            <CloseIcon fontSize="small"/>
          </IconButton>
        </div>

        {props.children}
      </Dialog>
    </Draggable>
  );
};

export const ProgressDialog = props => {
  const {open, text, percent, noTransition} = props;
  const classes = useStyles();

  return (
    <Dialog open={open}>
      <DialogContent className={classes.progressContent}>
        <DialogContentText>
          {text} <b>{percent.toFixed(2)}%</b>
        </DialogContentText>
        <LinearProgress
          variant="determinate"
          value={percent}
          classes={{
            bar: clsx("", {
              [classes.progressNoTransition]: noTransition
            })
          }}
          color="secondary"
        />
      </DialogContent>
    </Dialog>
  );
};
